/**
 * BackToTop — Premium floating back-to-top button.
 * Appears after scrolling past hero. Custom SVG arrow with
 * glassmorphism, floating idle animation, click ripple,
 * smooth eased scroll. GPU-accelerated.
 */

import { useState, useEffect, useCallback, useRef } from 'react';

export function BackToTop(): JSX.Element {
  const [visible, setVisible] = useState(false);
  const [clicked, setClicked] = useState(false);
  const [hovered, setHovered] = useState(false);
  const rippleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => {
      setVisible(window.scrollY > 600);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToTop = useCallback(() => {
    setClicked(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Reset clicked state after animation
    setTimeout(() => setClicked(false), 1000);
  }, []);

  return (
    <button
      onClick={scrollToTop}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      aria-label="Back to top"
      className={`fixed bottom-6 right-6 sm:bottom-8 sm:right-8 z-50 group outline-none transition-all duration-500 ${
        visible
          ? 'opacity-100 translate-y-0 pointer-events-auto'
          : 'opacity-0 translate-y-4 pointer-events-none'
      }`}
      style={{ willChange: 'transform, opacity' }}
    >
      {/* Outer container with floating animation */}
      <div className={`relative w-12 h-12 sm:w-14 sm:h-14 ${!clicked ? 'animate-[floatBtn_3s_ease-in-out_infinite]' : ''}`}>

        {/* Click ripple effect */}
        {clicked && (
          <div className="absolute inset-0 rounded-full animate-[rippleOut_0.8s_ease-out_forwards]">
            <div className="w-full h-full rounded-full border-2 border-blue-400/60" />
          </div>
        )}

        {/* Rotating outer ring */}
        <svg
          className={`absolute inset-0 w-full h-full transition-transform duration-700 ${hovered ? 'scale-110' : 'scale-100'}`}
          viewBox="0 0 56 56"
        >
          <circle
            cx="28" cy="28" r="26"
            fill="none"
            className="stroke-blue-500/20 dark:stroke-blue-400/20"
            strokeWidth="1"
            strokeDasharray="6 4"
          >
            <animateTransform attributeName="transform" type="rotate" from="0 28 28" to="360 28 28" dur="20s" repeatCount="indefinite" />
          </circle>
        </svg>

        {/* Main button body — glassmorphism */}
        <div
          className={`absolute inset-1 rounded-full transition-all duration-300 ${
            hovered
              ? 'shadow-[0_8px_30px_-5px_rgba(59,130,246,0.5)] scale-105'
              : 'shadow-[0_4px_20px_-5px_rgba(59,130,246,0.3)]'
          }`}
          style={{
            background: 'linear-gradient(135deg, rgba(59,130,246,0.9) 0%, rgba(37,99,235,0.95) 100%)',
            backdropFilter: 'blur(8px)',
          }}
        >
          {/* Inner highlight */}
          <div className="absolute inset-0 rounded-full overflow-hidden">
            <div className="absolute top-0 left-1/4 w-1/2 h-1/3 bg-white/20 rounded-full blur-sm" />
          </div>

          {/* Custom arrow illustration */}
          <svg
            viewBox="0 0 24 24"
            className={`relative w-full h-full p-2.5 sm:p-3 transition-transform duration-300 ${
              hovered ? '-translate-y-0.5 rotate-[-4deg]' : ''
            } ${clicked ? 'animate-[pulseUp_0.5s_ease-out]' : ''}`}
          >
            {/* Abstract geometric arrow */}
            <path
              d="M12 4L5 12h4v6h6v-6h4L12 4z"
              fill="white"
              fillOpacity="0.95"
            />
            {/* Accent line below arrow */}
            <rect x="8" y="20" width="8" height="1.5" rx="0.75" fill="white" fillOpacity="0.5" />
          </svg>
        </div>

        {/* Orbiting accent dot */}
        <div className={`absolute w-2 h-2 rounded-full bg-cyan-400 shadow-[0_0_6px_rgba(34,211,238,0.6)] transition-opacity duration-300 ${hovered ? 'opacity-100' : 'opacity-60'}`}
          style={{
            top: '2px',
            right: '2px',
            animation: 'orbitDot 4s linear infinite',
          }}
        />
      </div>
    </button>
  );
}
